import React from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { Edit, GripVertical, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { fetchJson } from '@utils/fetchJson';
import { API_BASE_URL } from '../../../config';
import Swal from 'sweetalert2';

export default function DroppableColumn({ rowIndex, colIndex, fields,onDeleteField, updateFields,onEdit }) {
  const droppableId = `row-${rowIndex}-col-${colIndex}`;
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    data: { type: 'column', rowIndex, colIndex },
  });

  const handleRemove = (i) => {
    const updated = [...fields];
    updated.splice(i, 1);
    updateFields(updated);
  };

  // const handleDeleteFromDB = async (field) => {
  //   if (!window.confirm(`Delete "${field.fieldLabel}" permanently?`)) return;
  //   try {
  //     const res = await fetchJson(`${API_BASE_URL}/dynamicformfield/status`, {
  //       method: 'POST',
  //       body: JSON.stringify({ list: field.fieldID,action:"changeStatus",status:"delete" }),
  //     });
  //     if (res.flag === 'S') {
  //       onDeleteField(field);
  //       updateFields(fields.filter((f) => f.fieldID !== field.fieldID));
  //       toast.success('Deleted');
  //     } else toast.error(res.msg || 'Failed');
  //   } catch {
  //     toast.error('Server error');
  //   }
  // };
const handleDeleteFromDB = async (field) => {
  const result = await Swal.fire({
    title: `Delete “${field.fieldLabel}”?`,
    html: `
      <div style="text-align:left">
        <p>This will <b>permanently delete</b> the field and all its stored data across records.</p>
        <p style="color:#b91c1c"><b>This action cannot be undone.</b> You will not be able to retrieve the data again.</p>
      </div>
    `,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete permanently',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#d33',
    reverseButtons: true,
    focusCancel: true,
  });

  if (!result.isConfirmed) return;

  // Optional: show a modal loader while deleting
  await Swal.fire({
    title: 'Deleting…',
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => Swal.showLoading(),
  });

  try {
    const res = await fetchJson(`${API_BASE_URL}/dynamicformfield/status`, {
      method: 'POST',
      body: JSON.stringify({ list: field.fieldID, action: 'changeStatus', status: 'delete' }),
      // headers: { 'Content-Type': 'application/json' }, // add if your fetchJson doesn't set it
    });

    Swal.close();

    if (res.flag === 'S') {
      updateFields(field);
      toast.success('Field deleted permanently.');
    } else {
      toast.error(res.msg || 'Failed to delete field.');
    }
  } catch (e) {
    Swal.close();
    toast.error('Server error while deleting.');
  }
};
  return (
    <div
      ref={setNodeRef}
      className={`min-h-[85px] border-2 rounded p-2 transition-all ${
        isOver ? 'border-blue-400 bg-blue-50' : 'border-dashed border-gray-300 bg-gray-50'
      }`}
    >
      {fields.length === 0 && (
        <div className="text-sm text-gray-400 text-center py-4">Drop field here</div>
      )}

      {fields.map((field, idx) => (
        <DroppedField
          key={field.fieldID}
          field={field}
          rowIndex={rowIndex}
          colIndex={colIndex}
          onRemove={() => handleRemove(idx)}
          onDelete={handleDeleteFromDB}
          onEdit={()=>{onEdit?.(field)}}
        />
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// NOTE: We’ve moved DroppedField here, so it can pick up rowIndex/colIndex props.
// ──────────────────────────────────────────────────────────────────────────────
const DroppedField = ({ field, rowIndex, colIndex, onRemove, onDelete, onEdit }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `dropped-${field.fieldID}`,
    data: {
      type: 'field',
      field,
      source: { rowIndex, colIndex },
    },
  });

  const style = {
    transform: transform
      ? `translate(${transform.x}px, ${transform.y}px)`
      : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-2 mb-2 rounded bg-white shadow text-sm border"
    >
      <div className="flex items-center gap-2">
        {/* Only Grip is draggable */}
        <span {...attributes} {...listeners} className="cursor-move text-gray-400">
          <GripVertical size={16} />
        </span>
        <div>
          <div className="font-medium text-gray-800">{field.fieldLabel}</div>
          <div className="text-xs text-gray-500">{field.fieldType}</div>
        </div>
      </div>
      <div className="flex gap-2">
        {/* <button onClick={() => onEdit(field)} title="Edit Field" className="text-blue-500">
          <Edit size={16} />
        </button> */}
        <button onClick={() => onDelete(field)} title="Delete Field" className="text-red-500">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};
